import { debug } from '../utils/debug';
/**
 * Adapters to transform API responses to match legacy data structure
 * This allows gradual migration from hardcoded data to Supabase
 */

import { supabase } from '../utils/supabaseClient'
import { comparePricingRules, pricingRuleAppliesToLocation } from '../utils/pricingRules'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Define rentable product types (all types that behave like bikes in the rental flow)
// These will appear in the product selection step
const RENTABLE_PRODUCT_TYPES = ['bike', 'sup', 'kayak', 'other'];

/**
 * Transform location from API to UI format
 */
export function adaptLocation(apiLocation) {
  const openingHours = apiLocation.opening_hours

  // Format opening hours string (e.g., "Monday: 08:00 - 20:00")
  const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
  const todayHours = openingHours?.[todayName]

  const hoursString = todayHours
    ? `${todayHours.open} - ${todayHours.close}`
    : '9:00 AM - 6:00 PM' // Fallback

  return {
    id: apiLocation.id,
    name: apiLocation.location_name,
    slug: apiLocation.location_slug, // URL-friendly slug for deep-linking
    address: apiLocation.pickup_address || 'Address not available',
    hours: hoursString,
    rating: apiLocation.rating || null, // Rating from database (0.0-5.0), null if not set
    bikes: 20, // TODO: Calculate from inventory
    image: apiLocation.picture_url || null, // Use null instead of fallback to allow conditional rendering
    show_today_tomorrow: apiLocation.show_today_tomorrow ?? true, // Whether to show Today/Tomorrow buttons
    // Opening hours (JSONB keyed by lowercase day name).
    // Persisted at top level so it survives the sessionStorage round-trip
    // where _apiData is stripped. Components should read
    //   location._apiData?.opening_hours || location.opening_hours
    opening_hours: openingHours || null,
    // Pickup hours and timeslot interval. Same sessionStorage
    // persistence rationale as opening_hours above.
    pickup_hours: apiLocation.pickup_hours || null,
    // Location toggle: when true, pickup/delivery times are restricted to those
    // whose rental return falls within opening hours. Mirrored top-level for the
    // sessionStorage round-trip (see opening_hours rationale above).
    restrict_handoffs_to_opening_hours: apiLocation.restrict_handoffs_to_opening_hours ?? false,
    timeslot_interval_minutes: apiLocation.timeslot_interval_minutes || 60,
    // Product display mode ('grouped' or 'mixed'). Same sessionStorage
    // persistence rationale as opening_hours above.
    product_display_mode: apiLocation.product_display_mode || null,
    // Tax configuration
    tax_rate: apiLocation.tax_rate || 0,
    tax_included: apiLocation.tax_included !== false, // Default to true (EU style)
    tax_name: apiLocation.tax_name || 'Tax',
    // Delivery configuration
    deliveries: apiLocation.deliveries || false,
    delivery_pricing_type: apiLocation.delivery_pricing_type || null,
    delivery_distance_unit: apiLocation.delivery_distance_unit || null,
    delivery_pricing_model: apiLocation.delivery_pricing_model || null,
    delivery_price_per_unit: apiLocation.delivery_price_per_unit || null,
    delivery_price_brackets: apiLocation.delivery_price_brackets || null,
    delivery_max_service_area: apiLocation.delivery_max_service_area || null,
    delivery_origin: apiLocation.delivery_origin || null,
    // Custom checkout fields (up to 2 per location)
    custom_fields: apiLocation.custom_fields || [],
    // Translation data for LanguageContext
    location_translations: apiLocation.location_translations || [],
    // Preserve full API data for backend calls
    _apiData: apiLocation
  }
}

/**
 * Transform product type from API to UI format
 */
export function adaptProductType(apiProduct) {
  // Get featured pricing from ACTIVE rules only (filter out inactive ones)
  const pricingRules = apiProduct.pricing_rules || []
  const activePricingRules = pricingRules.filter(p => p.active !== false)
  const featuredPricing = activePricingRules.find(p => p.featured) || activePricingRules[0]

  // Parse variants (sizes, colors, etc.) from JSONB
  // NEW: Support flexible variant system - variant_type determines if/what type of variant
  const variantType = apiProduct.variant_type // 'size', 'color', 'number', 'model', 'none', or null
  const hasVariants = variantType && variantType !== 'none'

  let variants = []
  if (hasVariants && apiProduct.sizes && typeof apiProduct.sizes === 'object') {
    // If sizes/variants is an array of variant objects
    if (Array.isArray(apiProduct.sizes)) {
      variants = apiProduct.sizes.map(v => ({
        value: v.value || v.size || v,              // Primary value (M, red, 1, etc.)
        description: v.description || v.height || '' // Description (160-175cm, Bright Red, etc.)
      }))
    } else {
      // If sizes/variants is an object with variant keys
      variants = Object.keys(apiProduct.sizes).map(key => ({
        value: key,
        description: apiProduct.sizes[key]
      }))
    }
  }

  // For backward compatibility, also provide 'sizes' in old format
  const sizes = variants.map(v => ({
    size: v.value,
    height: v.description
  }))

  const adapted = {
    id: apiProduct.id,
    name: apiProduct.product_name,
    category_id: apiProduct.category_id,  // Changed from 'category' to 'category_id' (UUID FK)
    category_slug: apiProduct.category_slug || null, // Category slug for deposit rule matching
    model: apiProduct.model,
    product_type: apiProduct.product_type, // bike, sup, kayak, other, addon
    price: featuredPricing?.price || 0,
    image: apiProduct.picture_url || 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop',
    sizes: sizes.length > 0 ? sizes : [], // Empty array if no sizes/variants defined
    features: apiProduct.features || [],
    description: apiProduct.description || '',
    popular: apiProduct.featured || false, // Use product_types.featured instead of pricing_rules.featured
    availableAddons: apiProduct.availableAddons || [], // From product_addons relationship
    recommendedAddons: apiProduct.recommendedAddons || [], // Which add-ons trigger modal
    preBookingHours: apiProduct.pre_booking_hours || 0, // Minimum hours advance notice required
    cutoffDaysPrior: apiProduct.cutoff_days_prior || null, // Days before rental for cutoff deadline
    cutoffTime: apiProduct.cutoff_time || null, // Time of day for cutoff (e.g., "17:00:00")
    delivery_eligible: apiProduct.delivery_eligible || false, // Whether product can be delivered
    // Show "Available" instead of the remaining count (display only). Top level so
    // it survives the sessionStorage round-trip where _apiData is stripped.
    hideStockCount: apiProduct.hide_stock_count === true,
    // NEW: Flexible variant system
    hasVariants: hasVariants, // Whether this product has variants (size, color, etc.)
    variantType: variantType, // Type of variant: 'size', 'color', 'number', 'model', 'none', or null
    variantLabel: apiProduct.variant_label || (variantType ? `Select ${variantType.charAt(0).toUpperCase() + variantType.slice(1)}` : null),
    variantDescriptionLabel: apiProduct.variant_description_label || null, // e.g., "Rider Height", "Color", "Number"
    variants: variants, // Array of {value, description}
    // Multi-language translations (from product_type_translations table)
    translations: apiProduct.translations || [], // Array of translation objects for non-default languages
    // Preserve full API data including all pricing rules
    _apiData: apiProduct,
    _pricingRules: pricingRules.map(pr => ({
      ...pr,
      // Include pricing rule translations
      translations: pr.translations || []
    }))
  }

  return adapted
}

/**
 * Fetch locations from API
 */
export async function fetchLocationsFromAPI(merchantId = null) {
  try {
    const url = merchantId
      ? `${API_BASE_URL}/locations?merchantId=${merchantId}`
      : `${API_BASE_URL}/locations`

    debug.log('Fetching from URL:', url)
    const response = await fetch(url)

    if (!response.ok) {
      // Provide more detailed error messages for common HTTP status codes
      if (response.status === 401) {
        throw new Error(`Authentication error when fetching locations for merchant: ${merchantId}`)
      } else if (response.status === 404) {
        throw new Error(`Merchant not found: ${merchantId}`)
      }
      throw new Error(`Failed to fetch locations: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    debug.log('API response data:', data)

    if (!data.success || !data.locations) {
      throw new Error('Invalid API response format')
    }

    debug.log('Number of locations received:', data.locations.length)
    debug.log('Raw locations data:', data.locations)

    const adaptedLocations = data.locations.map(adaptLocation)
    debug.log('Adapted locations:', adaptedLocations)

    // Return both locations and featured pricing rules
    return {
      locations: adaptedLocations,
      featuredPricingRules: data.featuredPricingRules || []
    }
  } catch (error) {
    console.error('Error fetching locations from API:', error)
    throw error
  }
}

/**
 * Fetch product types from API
 * Uses direct Supabase client if available, otherwise falls back to API endpoint
 * @param {string} locationId - Location UUID
 * @param {string[]|null} categoryIds - Array of category UUIDs to filter by (or null for all)
 */
export async function fetchProductTypesFromAPI(locationId, categoryIds = null) {
  const startTime = performance.now()
  debug.log('⏱️ [TIMING] Starting product-types fetch...')

  try {
    // Use RPC function to support multi-location products
    // Fetch all product types, then filter for rentable ones (bike, sup, kayak, other)
    const { data, error } = await supabase
      .rpc('get_product_types_by_location', {
        p_location_id: locationId,
        p_product_type: null,  // Fetch all types, filter below
        p_category: null  // Don't use legacy category filtering
      })

    const fetchTime = performance.now() - startTime
    debug.log(`⏱️ [TIMING] Product-types RPC fetch completed: ${fetchTime.toFixed(0)}ms`)

    if (error) {
      console.error('Supabase RPC error:', error)
      throw error
    }

    // Filter for rentable product types only (exclude addons)
    let rentableProducts = data.filter(p => RENTABLE_PRODUCT_TYPES.includes(p.product_type));

    // Filter by category IDs if provided (client-side filtering)
    if (Array.isArray(categoryIds) && categoryIds.length > 0) {
      rentableProducts = rentableProducts.filter(p => categoryIds.includes(p.category_id))
      debug.log(`⏱️ [TIMING] Filtered by ${categoryIds.length} category IDs: ${rentableProducts.length} products remaining`)
    }

    // Fetch pricing rules separately (RPC doesn't include them)
    // Filter by location: only include rules where location_ids is NULL/empty OR contains the current locationId
    const productIds = rentableProducts.map(p => p.id)
    let pricingRulesData = []

    if (productIds.length > 0) {
      const { data: pricing, error: pricingError } = await supabase
        .from('pricing_rules')
        .select('*')
        .in('product_type_id', productIds)
        .contains('channels', ['online'])
        .or(`location_ids.is.null,location_ids.cs.["${locationId}"]`)
        .order('display_order')

      if (pricingError) {
        console.error('Error fetching pricing rules:', pricingError)
        throw new Error(`Failed to fetch pricing rules: ${pricingError.message}`)
      } else {
        pricingRulesData = pricing || []
      }
    }

    // Fetch product_type_translations for all products
    let productTranslationsData = []
    if (productIds.length > 0) {
      const { data: translations, error: translationsError } = await supabase
        .from('product_type_translations')
        .select('*')
        .in('product_type_id', productIds)

      if (translationsError) {
        debug.warn('Error fetching product translations (table may not exist yet):', translationsError.message)
      } else {
        productTranslationsData = translations || []
      }
    }

    // Fetch pricing_rule_translations for all pricing rules
    let pricingTranslationsData = []
    const pricingRuleIds = pricingRulesData.map(pr => pr.id)
    if (pricingRuleIds.length > 0) {
      const { data: pricingTranslations, error: pricingTranslationsError } = await supabase
        .from('pricing_rule_translations')
        .select('*')
        .in('pricing_rule_id', pricingRuleIds)

      if (pricingTranslationsError) {
        debug.warn('Error fetching pricing translations (table may not exist yet):', pricingTranslationsError.message)
      } else {
        pricingTranslationsData = pricingTranslations || []
      }
    }

    // Merge pricing rules and translations into products
    const productsWithPricing = rentableProducts.map(product => ({
      ...product,
      pricing_rules: pricingRulesData
        .filter(pr => pr.product_type_id === product.id)
        .map(pr => ({
          ...pr,
          // Add translations for this pricing rule
          translations: pricingTranslationsData.filter(pt => pt.pricing_rule_id === pr.id)
        }))
        .sort((a, b) => a.display_order - b.display_order),
      // Add translations for this product
      translations: productTranslationsData.filter(pt => pt.product_type_id === product.id)
    }))

    // Add availableAddons field for all rentable products (not just bikes)
    const rentableProductIds = productsWithPricing.map(p => p.id)
    let productTypesWithAddons = productsWithPricing

    if (rentableProductIds.length > 0) {
      try {
        const { data: addonsData, error: addonsError } = await supabase
          .from('product_addons')
          .select('product_type_id, addon_id, recommended')
          .in('product_type_id', rentableProductIds)

        if (!addonsError && addonsData) {
          productTypesWithAddons = productsWithPricing.map(product => ({
            ...product,
            availableAddons: addonsData
              .filter(rel => rel.product_type_id === product.id)
              .map(rel => rel.addon_id),
            recommendedAddons: addonsData
              .filter(rel => rel.product_type_id === product.id && rel.recommended)
              .map(rel => rel.addon_id)
          }))
        }
      } catch (err) {
        debug.warn('Failed to fetch product add-ons relationships:', err.message)
      }
    }

    // Fetch category slugs for deposit rule matching
    const uniqueCatIds = [...new Set(productTypesWithAddons.map(p => p.category_id).filter(Boolean))]
    if (uniqueCatIds.length > 0) {
      const { data: categoriesData } = await supabase
        .from('categories')
        .select('id, category_slug')
        .in('id', uniqueCatIds)

      if (categoriesData) {
        const slugMap = Object.fromEntries(categoriesData.map(c => [c.id, c.category_slug]))
        productTypesWithAddons.forEach(p => {
          if (p.category_id && slugMap[p.category_id]) {
            p.category_slug = slugMap[p.category_id]
          }
        })
      }
    }

    const adaptStart = performance.now()
    const adapted = productTypesWithAddons.map(adaptProductType)
    const adaptTime = performance.now() - adaptStart
    debug.log(`⏱️ [TIMING] Product-types adaptation (${productTypesWithAddons.length} bikes): ${adaptTime.toFixed(0)}ms`)

    const totalTime = performance.now() - startTime
    debug.log(`⏱️ [TIMING] Total product-types time: ${totalTime.toFixed(0)}ms`)

    return adapted
  } catch (error) {
    console.error('Error fetching product types:', error)
    throw error
  }
}

/**
 * Fetch add-ons from API
 * Uses direct Supabase client if available, otherwise falls back to API endpoint
 */
export async function fetchAddonsFromAPI(locationId, productId = null) {
  const startTime = performance.now()
  debug.log('⏱️ [TIMING] Starting add-ons fetch...')

  try {
    // Use RPC function to support multi-location add-ons
    const { data, error } = await supabase
      .rpc('get_product_types_by_location', {
        p_location_id: locationId,
        p_product_type: 'addon',
        p_category: null
      })

    const fetchTime = performance.now() - startTime
    debug.log(`⏱️ [TIMING] Add-ons RPC fetch completed: ${fetchTime.toFixed(0)}ms`)

    if (error) {
      console.error('Supabase RPC error for add-ons:', error)
      throw error
    }

    // Fetch pricing rules separately (RPC doesn't include them)
    // Filter by location: only include rules where location_ids is NULL/empty OR contains the current locationId.
    // Done here rather than in the query: an EMPTY array means "all locations"
    // (migration 038, and calculate_rental_price), which the query's
    // `location_ids.cs.[id]` excluded.
    const addonIds = data.map(a => a.id)
    let pricingRulesData = []

    if (addonIds.length > 0) {
      const { data: pricing, error: pricingError } = await supabase
        .from('pricing_rules')
        .select('*')
        .in('product_type_id', addonIds)
        .contains('channels', ['online'])

      if (pricingError) {
        console.error('Error fetching pricing rules for add-ons:', pricingError)
      } else {
        pricingRulesData = (pricing || []).filter(pr => pricingRuleAppliesToLocation(pr, locationId))
      }
    }

    // Fetch product_type_translations for all add-ons
    let addonTranslationsData = []
    if (addonIds.length > 0) {
      const { data: translations, error: translationsError } = await supabase
        .from('product_type_translations')
        .select('*')
        .in('product_type_id', addonIds)

      if (translationsError) {
        debug.warn('Error fetching addon translations (table may not exist yet):', translationsError.message)
      } else {
        addonTranslationsData = translations || []
      }
    }

    // Fetch pricing_rule_translations for all pricing rules
    let pricingTranslationsData = []
    const pricingRuleIds = pricingRulesData.map(pr => pr.id)
    if (pricingRuleIds.length > 0) {
      const { data: pricingTranslations, error: pricingTranslationsError } = await supabase
        .from('pricing_rule_translations')
        .select('*')
        .in('pricing_rule_id', pricingRuleIds)

      if (pricingTranslationsError) {
        debug.warn('Error fetching pricing translations for addons (table may not exist yet):', pricingTranslationsError.message)
      } else {
        pricingTranslationsData = pricingTranslations || []
      }
    }

    // Merge pricing rules and translations into add-ons
    let addonsWithPricing = data.map(addon => ({
      ...addon,
      pricing_rules: pricingRulesData
        .filter(pr => pr.product_type_id === addon.id)
        .map(pr => ({
          ...pr,
          // Add translations for this pricing rule
          translations: pricingTranslationsData.filter(pt => pt.pricing_rule_id === pr.id)
        }))
        // The order calculate_rental_price uses (location-specific first, then
        // display_order, price, id) — shared with the manage-booking app.
        .sort(comparePricingRules),
      // Add translations for this addon
      translations: addonTranslationsData.filter(at => at.product_type_id === addon.id)
    }))

    // Filter by product relationship if productId provided
    if (productId) {
      const { data: relationships } = await supabase
        .from('product_addons')
        .select('addon_id')
        .eq('product_type_id', productId)

      if (relationships && relationships.length > 0) {
        const relatedAddonIds = relationships.map(r => r.addon_id)
        addonsWithPricing = addonsWithPricing.filter(addon => relatedAddonIds.includes(addon.id))
      }
    }

    const totalTime = performance.now() - startTime
    debug.log(`⏱️ [TIMING] Total add-ons time (${addonsWithPricing.length} items): ${totalTime.toFixed(0)}ms`)

    return addonsWithPricing
  } catch (error) {
    console.error('Error fetching add-ons:', error)
    throw error
  }
}

/**
 * Format add-ons for AddonsModal component
 * Converts API format to the format expected by the UI
 */
export function formatAddonsForUI(apiAddons) {
  const addonsObject = {}

  apiAddons.forEach(addon => {
    // Skip inactive addons entirely - they should not appear in the UI
    if (addon.active === false) {
      debug.log(`🔍 [formatAddonsForUI] Skipping inactive addon: ${addon.product_name} (${addon.id})`)
      return
    }

    // Get the first ACTIVE pricing rule (filter out inactive ones)
    const activePricingRules = addon.pricing_rules?.filter(pr => pr.active !== false) || []

    // Prefer featured pricing, otherwise take first active rule
    const pricing = activePricingRules.find(pr => pr.featured) || activePricingRules[0] || null

    addonsObject[addon.id] = {
      id: addon.id,
      name: addon.product_name,
      description: addon.description || '',
      price: pricing?.price || 0,
      priceType: 'fixed', // Add-ons are typically fixed price per rental
      currency: pricing?.currency || 'EUR',
      category: addon.category,
      picture_url: addon.picture_url || null, // Add-on image
      featured: addon.featured || false, // Whether to show badge (recommended/popular)
      recommended: addon.recommended || false, // From product_addons relationship
      inventory_type: addon.inventory_type || 'unlimited', // CRITICAL: For pool quantity validation
      pool_quantity: addon.pool_quantity || null, // CRITICAL: Maximum available for pool items
      preBookingHours: addon.pre_booking_hours || 0, // CRITICAL: Minimum hours advance notice required
      // Cutoff deadline (e.g. "no later than 2 days before, by 17:00"). Use ?? so a
      // legitimate cutoff_days_prior of 0 (same-day cutoff) is preserved, not coerced to null.
      cutoffDaysPrior: addon.cutoff_days_prior ?? null,
      cutoffTime: addon.cutoff_time ?? null,
      display_order: addon.display_order || 0, // CRITICAL: For sorting addons in display
      // Multi-language translations (from product_type_translations table)
      translations: addon.translations || [], // Array of translation objects for non-default languages
      // Preserve raw data
      _apiData: addon,
      // CRITICAL: Preserve ALL pricing rules for duration-based pricing (includes ACTIVE rules only in activePricingRules, but keep all for reference)
      _pricingRules: addon.pricing_rules || []
    }
  })

  debug.log('Formatted add-ons for UI:', addonsObject)
  return addonsObject
}

/**
 * Fetch product-addon relationships for a specific location
 * Uses RPC function that handles multi-location products via available_location_ids
 *
 * @param {string} locationId - UUID of the location
 * @returns {Promise<Array>} Array of relationships with product_type_id, addon_id, recommended
 */
export async function fetchProductAddonRelationships(locationId) {
  const startTime = performance.now()
  debug.log('⏱️ [TIMING] Starting product-addon relationships fetch...')

  try {
    const { data, error } = await supabase
      .rpc('get_product_addons_for_location', {
        p_location_id: locationId
      })

    const fetchTime = performance.now() - startTime
    debug.log(`⏱️ [TIMING] Product-addon relationships RPC completed: ${fetchTime.toFixed(0)}ms`)

    if (error) {
      console.error('Supabase RPC error for product-addon relationships:', error)
      throw error
    }

    debug.log(`✅ Fetched ${data?.length || 0} product-addon relationships for location`)

    return data || []
  } catch (error) {
    console.error('Error fetching product-addon relationships:', error)
    // Return null on error to signal we couldn't verify - will show all add-ons (safe fallback)
    return null
  }
}

/**
 * Generate filter options from products
 * @param {Array} products - Array of product objects
 * @param {Array} categoryObjects - Array of category objects with translations
 * @param {string} currentLanguage - Current language code (e.g., 'en', 'de')
 * @param {string} defaultLanguage - Default/fallback language code
 */
export function generateFilterOptions(products, categoryObjects = [], currentLanguage = 'en', defaultLanguage = 'en') {
  debug.log('Generating filter options from products:', products)

  // Helper to get translated category name
  const getCategoryName = (category) => {
    if (!category.category_translations || category.category_translations.length === 0) {
      return category.category_name;
    }

    // Try current language
    const currentTranslation = category.category_translations.find(
      t => t.language_code === currentLanguage
    );
    if (currentTranslation) return currentTranslation.category_name;

    // Try default language
    const defaultTranslation = category.category_translations.find(
      t => t.language_code === defaultLanguage
    );
    if (defaultTranslation) return defaultTranslation.category_name;

    // Fallback to original name
    return category.category_name;
  };

  // Category options are {value, label} pairs. The value is the category id
  // (language-independent, stored in filters.category); the label is the
  // translated display name. The empty value means "All Categories" —
  // consumers render its label via i18n.
  const categories = [{ value: '', label: 'All Categories' }]
  const bikeModels = ['All Models']
  const sizes = ['All Sizes']

  products.forEach(product => {
    debug.log('Processing product:', {
      name: product.name,
      category_id: product.category_id,
      model: product.model,
      sizes: product.sizes
    })

    // Look up category name from category_id
    if (product.category_id && categoryObjects.length > 0) {
      const categoryObj = categoryObjects.find(c => c.id === product.category_id);
      if (categoryObj) {
        if (!categories.some(c => c.value === categoryObj.id)) {
          categories.push({ value: categoryObj.id, label: getCategoryName(categoryObj) });
          debug.log('Added category:', categoryObj.id);
        }
      }
    }

    if (product.name && !bikeModels.includes(product.name)) {
      bikeModels.push(product.name)
      debug.log('Added bike model:', product.name)
    }
    if (product.sizes && Array.isArray(product.sizes)) {
      product.sizes.forEach(sizeInfo => {
        const sizeMap = {
          'S': 'Small (S)',
          'M': 'Medium (M)',
          'L': 'Large (L)',
          'XL': 'Extra Large (XL)',
        }
        const displaySize = sizeMap[sizeInfo.size] || sizeInfo.size
        if (!sizes.includes(displaySize)) {
          sizes.push(displaySize)
          debug.log('Added size:', displaySize)
        }
      })
    } else {
      debug.log('No sizes array found for product:', product.name)
    }
  })

  // Order the category options by categories.display_order (the list is
  // collected in product-iteration order above). 'All Categories' stays first;
  // remaining entries are sorted by their category's display_order, with the
  // label as a stable tie-break since display_order has no unique key.
  const orderById = new Map(
    categoryObjects.map(c => [c.id, c.display_order ?? Infinity])
  );
  const [allCategoriesEntry, ...categoryEntries] = categories;
  categoryEntries.sort((a, b) => {
    const orderA = orderById.has(a.value) ? orderById.get(a.value) : Infinity;
    const orderB = orderById.has(b.value) ? orderById.get(b.value) : Infinity;
    if (orderA !== orderB) return orderA - orderB;
    return a.label.localeCompare(b.label);
  });
  const sortedCategories = [allCategoriesEntry, ...categoryEntries];

  const result = { categories: sortedCategories, bikeModels, sizes }
  debug.log('Generated filter options:', result)
  return result
}

/**
 * Check availability from API
 * @param {string} excludedInventoryItemIds - Optional array of inventory item IDs to exclude (items already in basket)
 */
export async function checkAvailabilityFromAPI(locationId, startDate, endDate, filters = {}, excludedInventoryItemIds = null) {
  const startTime = performance.now()

  try {
    // Handle both Date objects and strings
    const startDateStr = typeof startDate === 'string' ? startDate : startDate.toISOString();
    const endDateStr = typeof endDate === 'string' ? endDate : endDate.toISOString();

    {
      debug.log('🔷 [AVAILABILITY] Single availability check')
      debug.log('🔷 [AVAILABILITY] Location:', locationId)
      debug.log('🔷 [AVAILABILITY] Date range:', startDateStr, 'to', endDateStr)
      debug.log('🔷 [AVAILABILITY] Filters:', filters)

      // DEBUG: Check pre-booking hours timing
      const startDateObj = new Date(startDateStr)
      const nowDate = new Date()
      const hoursDifference = (startDateObj - nowDate) / (1000 * 60 * 60)
      debug.log('⏰ [PRE-BOOKING DEBUG - CLIENT]:', {
        now: nowDate.toISOString(),
        requestedStart: startDateObj.toISOString(),
        hoursUntilStart: hoursDifference.toFixed(2),
        note: 'If product has pre_booking_hours=2, hours must be >= 2.0'
      })

      if (excludedInventoryItemIds && excludedInventoryItemIds.length > 0) {
        debug.log('🔷 [DIRECT CLIENT] Excluding inventory items:', excludedInventoryItemIds.length, 'items')
      }

      const params = {
        p_location_id: locationId,
        p_start_date: startDateStr,
        p_end_date: endDateStr,
        p_category: filters.category || null,
        p_model: filters.model || null,
        p_size: filters.size || null,
        // IMPORTANT: Don't pass quantity to database
        // The DB function filters OUT product types that don't individually have enough quantity
        // Instead, we fetch ALL matching products and sum them on the frontend
        // This allows booking 3 bikes from 3 different models when "All Models" is selected
        p_quantity: null,
        p_product_type_id: filters.product_type_id || null,
        // SHARED INVENTORY FIX: Exclude items already in basket
        p_excluded_inventory_item_ids: (excludedInventoryItemIds && excludedInventoryItemIds.length > 0) ? excludedInventoryItemIds : null
      }

      debug.log('🔷 [DIRECT CLIENT] RPC params:', JSON.stringify(params, null, 2))

      const { data, error } = await supabase.rpc('check_availability_enhanced', params)

      const totalTime = performance.now() - startTime
      debug.log(`🔷 [DIRECT CLIENT] Availability check completed in ${totalTime.toFixed(0)}ms`)

      if (error) {
        console.error('🔷 [DIRECT CLIENT] RPC ERROR:', error)
        throw error
      }

      debug.log('🔷 [DIRECT CLIENT] Raw response:', data?.length || 0, 'individual items')

      // DEBUG: Log pre-booking hours from database response
      if (data && data.length > 0) {
        debug.log('⏰ [PRE-BOOKING] Database returned effective_pre_booking_hours:',
          data.map(item => ({
            product: item.product_name,
            size: item.size,
            effective_pre_booking_hours: item.effective_pre_booking_hours,
            item_id: item.item_id
          }))
        )
      }

      // BUNDLE COMPONENT FIX: If any results are bundles, fetch their component availability too
      let bundleComponentData = []

      if (data && Array.isArray(data) && data.length > 0) {
        const bundles = data.filter(item => item.is_bundle && item.bundle_components)

        debug.log('🎁 [BUNDLE FIX - DIRECT] Checking for bundles in', data.length, 'results')
        debug.log('🎁 [BUNDLE FIX - DIRECT] First result:', {
          product_type_id: data[0]?.product_type_id,
          product_name: data[0]?.product_name,
          is_bundle: data[0]?.is_bundle,
          bundle_components: data[0]?.bundle_components
        })

        if (bundles.length > 0) {
          debug.log('🎁 [BUNDLE FIX - DIRECT] Found', bundles.length, 'bundle(s), fetching components')

          // Extract all unique component product_type_ids
          const componentProductTypeIds = new Set()
          bundles.forEach(bundle => {
            debug.log('🎁 [BUNDLE FIX - DIRECT] Bundle:', bundle.product_name, 'components:', bundle.bundle_components)
            if (bundle.bundle_components && Array.isArray(bundle.bundle_components)) {
              bundle.bundle_components.forEach(comp => {
                debug.log('🎁 [BUNDLE FIX - DIRECT] Component:', comp)
                componentProductTypeIds.add(comp.product_type_id)
              })
            }
          })

          debug.log('🎁 [BUNDLE FIX - DIRECT] Component IDs:', Array.from(componentProductTypeIds))

          // Fetch availability for all component products WITHOUT category/model filters
          for (const componentId of componentProductTypeIds) {
            const componentParams = {
              p_location_id: locationId,
              p_start_date: startDateStr,
              p_end_date: endDateStr,
              p_category: null, // No category filter
              p_model: null, // No model filter
              p_size: filters.size || null, // Keep size filter if specified
              p_quantity: null,
              p_product_type_id: componentId,
              p_excluded_inventory_item_ids: (excludedInventoryItemIds && excludedInventoryItemIds.length > 0) ? excludedInventoryItemIds : null
            }

            debug.log('🎁 [BUNDLE FIX - DIRECT] Fetching component:', componentId)
            const { data: compData, error: compError } = await supabase.rpc('check_availability_enhanced', componentParams)

            if (compError) {
              console.error('🎁 [BUNDLE FIX - DIRECT] Error:', compError)
            } else if (compData) {
              bundleComponentData.push(...compData)
              debug.log('🎁 [BUNDLE FIX - DIRECT] Fetched', compData.length, 'entries for', componentId)
            }
          }

          debug.log('🎁 [BUNDLE FIX - DIRECT] Total component data:', bundleComponentData.length)
        }
      }

      // Merge bundle component data with main data (deduplicate by item_id)
      const seenItemIds = new Set()
      const allData = []

      // Add main data first
      if (data && Array.isArray(data)) {
        data.forEach(item => {
          if (item.item_id) {
            seenItemIds.add(item.item_id)
          }
          allData.push(item)
        })
      }

      // Add bundle component data, skip duplicates
      bundleComponentData.forEach(item => {
        if (item.item_id && seenItemIds.has(item.item_id)) {
          debug.log('🎁 [BUNDLE FIX - DIRECT] Skipping duplicate:', item.item_id)
        } else {
          if (item.item_id) {
            seenItemIds.add(item.item_id)
          }
          allData.push(item)
        }
      })

      debug.log('🎁 [BUNDLE FIX - DIRECT] Total data (merged):', allData.length)

      // AGGREGATE: The database function returns individual items
      // We need to group by product_type_id + size and count available items
      const aggregatedAvailability = {}

      if (allData && Array.isArray(allData)) {
        allData.forEach(item => {
          // Create unique key for product_type + size combination
          const key = `${item.product_type_id}_${item.size || 'null'}`

          if (!aggregatedAvailability[key]) {
            aggregatedAvailability[key] = {
              product_type_id: item.product_type_id,
              product_name: item.product_name,
              category: item.category,
              size: item.size,
              inventory_type: item.inventory_type,
              available_count: 0,
              available_items: [],
              // CRITICAL: Preserve bundle metadata from database
              is_bundle: item.is_bundle || false,
              bundle_components: item.bundle_components || null,
              picture_url: item.picture_url || null,
              // POOL ITEMS: Store pool_quantity if present
              pool_quantity: item.pool_quantity || null
            }
          }

          // For individual items: count rows (each row = 1 available item)
          // For pool/unlimited items: use pool_quantity field (only 1 row returned)
          if (item.inventory_type === 'individual') {
            aggregatedAvailability[key].available_count++
          } else if (item.pool_quantity) {
            // Pool or unlimited item - use pool_quantity from database
            aggregatedAvailability[key].available_count = item.pool_quantity
          }

          // Store item details if it's an individual item
          if (item.item_id) {
            aggregatedAvailability[key].available_items.push({
              item_id: item.item_id,
              item_number: item.item_number,
              combination_code: item.combination_code,
              nr: item.nr // Also preserve nr field for logging
            })
          }
        })
      }

      // Convert object to array
      const aggregatedArray = Object.values(aggregatedAvailability)

      debug.log('🔷 [DIRECT CLIENT] Aggregated to', aggregatedArray.length, 'product types')

      // Log bundles in final response
      const bundlesInResponse = aggregatedArray.filter(item => item.is_bundle)
      if (bundlesInResponse.length > 0) {
        debug.log('🎁 [BUNDLE FIX - DIRECT] Bundles in final response:', bundlesInResponse.map(b => ({
          product_type_id: b.product_type_id,
          product_name: b.product_name,
          available_count: b.available_count,
          bundle_components: b.bundle_components
        })))

        // Check component availability
        bundlesInResponse.forEach(bundle => {
          if (bundle.bundle_components && Array.isArray(bundle.bundle_components)) {
            debug.log('🎁 [BUNDLE FIX - DIRECT] Components for', bundle.product_name)
            bundle.bundle_components.forEach(comp => {
              const componentInResponse = aggregatedArray.find(item => item.product_type_id === comp.product_type_id)
              debug.log('🎁 [BUNDLE FIX - DIRECT]   Component', comp.product_type_id, ':',
                componentInResponse ? `✅ available (count: ${componentInResponse.available_count})` : '❌ NOT FOUND')
            })
          }
        })
      }

      if (aggregatedArray.length > 0) {
        debug.log('🔷 [DIRECT CLIENT] Products:', aggregatedArray.map(item => ({
          product_type_id: item.product_type_id,
          product_name: item.product_name,
          size: item.size,
          available_count: item.available_count
        })))
        const totalAvailable = aggregatedArray.reduce((sum, item) => sum + (item.available_count || 0), 0)
        debug.log('🔷 [DIRECT CLIENT] Total available (sum across all products):', totalAvailable)
      }

      return aggregatedArray
    }
  } catch (error) {
    console.error('Error checking availability:', error)
    throw error
  }
}

/**
 * Check availability for multiple time slots in one call
 */
export async function checkBatchAvailabilityFromAPI(locationId, timeSlots, filters = {}) {
  const startTime = performance.now()
  debug.log('⏱️ [TIMING] Starting batch availability check...')
  debug.log('⏱️ [TIMING] Checking', timeSlots.length, 'time slots')

  try {
    {
      debug.log('🔷 [BATCH] Location ID:', locationId)
      debug.log('🔷 [BATCH] Filters:', filters)
      debug.log('🔷 [BATCH] Number of time slots:', timeSlots.length)
      debug.log('🔷 [BATCH] Time slots:', timeSlots)

      // Check availability for each time slot using Promise.all for parallel execution
      const results = await Promise.all(
        timeSlots.map(async (slot) => {
          const slotStartTime = performance.now()
          try {
            debug.log(`\n🔷 [DIRECT CLIENT - SLOT ${slot.slotId}] Starting check...`)
            debug.log(`🔷 [DIRECT CLIENT - SLOT ${slot.slotId}] Time range: ${slot.start} to ${slot.end}`)

            // IMPORTANT: Don't pass quantity to the database function
            // The DB function filters OUT product types that don't individually have enough quantity
            // Instead, we fetch all matching products and sum them up here
            const params = {
              p_location_id: locationId,
              p_start_date: slot.start,
              p_end_date: slot.end,
              p_category: filters.category || null,
              p_model: filters.model || null,
              p_size: filters.size || null,
              p_quantity: null, // DO NOT PASS - we check quantity after summing
              p_product_type_id: null
            }

            debug.log(`🔷 [DIRECT CLIENT - SLOT ${slot.slotId}] RPC params:`, JSON.stringify(params, null, 2))

            const { data, error } = await supabase.rpc('check_availability_enhanced', params)

            const rpcTime = performance.now() - slotStartTime
            debug.log(`🔷 [DIRECT CLIENT - SLOT ${slot.slotId}] RPC completed in ${rpcTime.toFixed(0)}ms`)

            if (error) {
              console.error(`❌ [DIRECT CLIENT - SLOT ${slot.slotId}] RPC ERROR:`, error)
              console.error(`❌ [DIRECT CLIENT - SLOT ${slot.slotId}] Error message:`, error.message)
              console.error(`❌ [DIRECT CLIENT - SLOT ${slot.slotId}] Error details:`, error.details)
              console.error(`❌ [DIRECT CLIENT - SLOT ${slot.slotId}] Error hint:`, error.hint)
              console.error(`❌ [DIRECT CLIENT - SLOT ${slot.slotId}] Error code:`, error.code)
              return {
                slotId: slot.slotId,
                available: false,
                totalAvailable: 0,
                error: error.message
              }
            }

            debug.log(`🔷 [DIRECT CLIENT - SLOT ${slot.slotId}] Raw response: ${data?.length || 0} individual items`)

            // AGGREGATE: The database function returns individual items
            // We need to group by product_type_id + size and count available items
            const aggregatedAvailability = {}

            if (data && Array.isArray(data)) {
              data.forEach(item => {
                const key = `${item.product_type_id}_${item.size || 'null'}`

                if (!aggregatedAvailability[key]) {
                  aggregatedAvailability[key] = {
                    product_type_id: item.product_type_id,
                    product_name: item.product_name,
                    category: item.category,
                    category_id: item.category_id,
                    size: item.size,
                    inventory_type: item.inventory_type,
                    product_type: item.product_type, // 'bike', 'addon', 'scooter', etc.
                    available_count: 0,
                    available_items: [],
                    // PHASE 1: Item-level pre-booking - collect all effective values
                    effective_pre_booking_values: [],
                    effective_cutoff_values: []
                  }
                }

                // For pool/unlimited items, use pool_quantity
                if (item.inventory_type === 'pool' || item.inventory_type === 'unlimited') {
                  aggregatedAvailability[key].available_count = item.pool_quantity || 0
                  // Store effective pre-booking values (product_type level for pool/unlimited)
                  if (item.effective_pre_booking_hours !== null && item.effective_pre_booking_hours !== undefined) {
                    aggregatedAvailability[key].effective_pre_booking_values.push(item.effective_pre_booking_hours)
                  }
                  if (item.effective_cutoff_days_prior !== null && item.effective_cutoff_time !== null) {
                    aggregatedAvailability[key].effective_cutoff_values.push({
                      cutoff_days_prior: item.effective_cutoff_days_prior,
                      cutoff_time: item.effective_cutoff_time
                    })
                  }
                } else {
                  // Increment count for individual items
                  aggregatedAvailability[key].available_count++

                  if (item.item_id) {
                    aggregatedAvailability[key].available_items.push({
                      item_id: item.item_id,
                      item_number: item.item_number,
                      combination_code: item.combination_code,
                      // PHASE 1: Include item-level effective pre-booking values
                      effective_pre_booking_hours: item.effective_pre_booking_hours,
                      effective_cutoff_days_prior: item.effective_cutoff_days_prior,
                      effective_cutoff_time: item.effective_cutoff_time
                    })
                  }

                  // Collect effective pre-booking values for sorting logic
                  if (item.effective_pre_booking_hours !== null && item.effective_pre_booking_hours !== undefined) {
                    aggregatedAvailability[key].effective_pre_booking_values.push(item.effective_pre_booking_hours)
                  }
                  if (item.effective_cutoff_days_prior !== null && item.effective_cutoff_time !== null) {
                    aggregatedAvailability[key].effective_cutoff_values.push({
                      cutoff_days_prior: item.effective_cutoff_days_prior,
                      cutoff_time: item.effective_cutoff_time
                    })
                  }
                }
              })
            }

            const aggregatedArray = Object.values(aggregatedAvailability)

            // FILTER AGGREGATED RESULTS: Only count items that match ALL active filters
            // This prevents add-ons and unrelated products from being counted in availability
            let itemsToCount = aggregatedArray;

            // ALWAYS exclude addons from availability count for calendar/time selection
            // Addons (helmets, child seats, etc.) shouldn't make a day appear "available"
            // Only rentable products (bike, scooter, sup, kayak, other) count
            itemsToCount = itemsToCount.filter(item => item.product_type !== 'addon');

            // Filter by category if specified. Prefer category_id (what the
            // current RPC returns); the legacy name comparison only works for
            // callers whose items still carry a category name string.
            if (filters.category_id) {
              itemsToCount = itemsToCount.filter(item => item.category_id === filters.category_id);
            } else if (filters.category) {
              itemsToCount = itemsToCount.filter(item => item.category === filters.category);
            }

            // Filter by model if specified
            if (filters.model) {
              itemsToCount = itemsToCount.filter(item => item.product_name === filters.model);
            }

            // Filter by size if specified
            if (filters.size) {
              itemsToCount = itemsToCount.filter(item => item.size === filters.size);
            }

            // Calculate total available across matching product types
            // This allows booking 3 bikes from 3 different models when no specific model is selected
            const totalAvailable = itemsToCount.reduce((sum, item) => {
              return sum + (item.available_count || 0)
            }, 0)

            const requestedQuantity = filters.quantity || 1
            const available = totalAvailable >= requestedQuantity

            return {
              slotId: slot.slotId,
              available,
              totalAvailable,
              items: aggregatedArray
            }
          } catch (error) {
            console.error(`❌ [DIRECT CLIENT - SLOT ${slot.slotId}] EXCEPTION:`, error)
            return {
              slotId: slot.slotId,
              available: false,
              totalAvailable: 0,
              error: error.message
            }
          }
        })
      )

      const totalTime = performance.now() - startTime
      debug.log(`\n🔷 [DIRECT CLIENT] ===== BATCH COMPLETE =====`)
      debug.log(`🔷 [DIRECT CLIENT] Total time: ${totalTime.toFixed(0)}ms`)
      debug.log(`🔷 [DIRECT CLIENT] Average per slot: ${(totalTime / timeSlots.length).toFixed(0)}ms`)
      debug.log(`🔷 [DIRECT CLIENT] Results summary:`, results.map(r => ({
        slotId: r.slotId,
        available: r.available,
        totalAvailable: r.totalAvailable,
        itemCount: r.items?.length || 0
      })))

      return results
    }
  } catch (error) {
    console.error('Error checking batch availability:', error)
    throw error
  }
}

/**
 * Check if a given date has at least one available time slot
 * Stops checking as soon as one available slot is found (optimization)
 *
 * @param {Date} date - The date to check
 * @param {string} locationId - Location ID
 * @param {string} duration - Duration string (e.g., "4h", "9h", "1d")
 * @param {Object} openingHours - Opening hours object with days as keys
 * @param {Object} filters - Availability filters (category, model, size, quantity)
 * @returns {Promise<boolean>} True if at least one slot is available
 */
export async function checkDateHasAvailableSlot(date, locationId, duration, openingHours, filters = {}) {
  const startTime = performance.now()

  try {
    debug.log('📅 [DATE CHECK] Checking date:', date.toISOString().split('T')[0])

    // Helper to parse time string like "08:00" to hours (8)
    const parseTimeToHours = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number)
      return hours + minutes / 60
    }

    // Parse rental duration (e.g., "4h" -> 4, "1d" -> 24)
    const parseDuration = (durationStr) => {
      if (!durationStr) return 4 // Default 4 hours

      // Handle if duration is an object with id property
      const str = typeof durationStr === 'string' ? durationStr : durationStr?.id || durationStr?.value || ''

      if (typeof str !== 'string' || str === '') return 4

      if (str.endsWith('h')) {
        return parseInt(str)
      } else if (str.endsWith('d')) {
        return parseInt(str) * 24
      } else if (str.endsWith('w')) {
        return parseInt(str) * 24 * 7
      }
      return 4
    }

    const rentalDurationHours = parseDuration(duration)
    debug.log('📅 [DATE CHECK] Rental duration:', rentalDurationHours, 'hours')

    // Get day name and opening hours for that day
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const dayName = dayNames[date.getDay()]
    const dayHours = openingHours?.[dayName]

    if (!dayHours) {
      debug.log('📅 [DATE CHECK] No opening hours for', dayName, '- store closed')
      return false // Store closed on this day
    }

    const openHour = parseTimeToHours(dayHours.open)
    const closeHour = parseTimeToHours(dayHours.close)

    debug.log('📅 [DATE CHECK] Opening hours:', dayHours.open, '-', dayHours.close, `(${openHour} - ${closeHour})`)

    // Check if today - if so, only check future time slots
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const currentHour = now.getHours() + now.getMinutes() / 60

    debug.log('📅 [DATE CHECK] Current time:', now.toLocaleTimeString(), `(hour: ${currentHour.toFixed(2)})`)
    debug.log('📅 [DATE CHECK] Is today?', isToday)
    debug.log('📅 [DATE CHECK] Date being checked:', date.toISOString())

    const startHour = isToday
      ? Math.max(Math.ceil(currentHour), Math.floor(openHour))
      : Math.floor(openHour)

    debug.log('📅 [DATE CHECK] Start hour calculated:', startHour, '(isToday:', isToday, 'currentHour:', currentHour.toFixed(2), 'ceil:', Math.ceil(currentHour), 'openHour:', Math.floor(openHour), ')')
    debug.log('📅 [DATE CHECK] Will check slots from hour', startHour, 'to', closeHour, `(${closeHour - startHour} hours range)`)

    // Generate time slots based on rental duration
    const slots = []
    debug.log('📅 [DATE CHECK] Generating slots: startHour =', startHour, 'closeHour =', closeHour, 'rentalDuration =', rentalDurationHours, 'hours')
    for (let hour = startHour; hour < closeHour; hour++) {
      // For day/week rentals (24+ hours), all slots are valid
      if (rentalDurationHours >= 24) {
        debug.log(`📅 [DATE CHECK]   -> ${hour}:00 added (multi-day rental)`)
        slots.push(hour)
      } else {
        // For hourly rentals, ensure rental doesn't extend past closing
        const rentalEndHour = hour + rentalDurationHours
        if (rentalEndHour <= closeHour) {
          debug.log(`📅 [DATE CHECK]   -> ${hour}:00-${rentalEndHour}:00 added (ends before ${closeHour}:00)`)
          slots.push(hour)
        } else {
          debug.log(`📅 [DATE CHECK]   -> ${hour}:00-${rentalEndHour}:00 SKIPPED (ends after ${closeHour}:00)`)
        }
      }
    }

    debug.log('📅 [DATE CHECK] Generated', slots.length, 'time slots to check:', slots.map(h => `${h}:00`).join(', '))

    if (slots.length === 0) {
      debug.log('📅 [DATE CHECK] No valid time slots for this date')
      return false
    }

    // PARALLEL APPROACH: Check all time slots simultaneously
    // Use Promise.race to exit as soon as we find ONE available slot
    debug.log(`📅 [DATE CHECK] Checking ${slots.length} time slots in parallel...`)

    const requestedQuantity = filters.quantity || 1

    // Create a promise for each slot check (using async map function)
    const slotCheckPromises = slots.map(async (hour, index) => {
      try {
        // Create start and end times for this slot
        const slotStart = new Date(date)
        slotStart.setHours(hour, 0, 0, 0)

        const slotEnd = new Date(slotStart)
        slotEnd.setHours(hour + rentalDurationHours, 0, 0, 0)

        debug.log(`📅 [DATE CHECK] Slot ${index + 1}/${slots.length}: ${hour}:00 - Starting check`)

        // Check availability for this time slot
        const availability = await checkAvailabilityFromAPI(
          locationId,
          slotStart.toISOString(),
          slotEnd.toISOString(),
          filters
        )

        // Calculate total available across all product types
        const totalAvailable = availability?.reduce((sum, item) => {
          return sum + (item.available_count || 0)
        }, 0) || 0

        debug.log(`📅 [DATE CHECK] Slot ${hour}:00 - Available: ${totalAvailable}, Requested: ${requestedQuantity}`)

        // If this slot has enough availability, return success
        if (totalAvailable >= requestedQuantity) {
          const slotTime = performance.now() - startTime
          debug.log(`📅 [DATE CHECK] ✅ Slot ${hour}:00 is available! (${slotTime.toFixed(0)}ms)`)
          return { available: true, hour, totalAvailable }
        } else {
          debug.log(`📅 [DATE CHECK] ❌ Slot ${hour}:00 not available`)
          return { available: false, hour, totalAvailable }
        }
      } catch (error) {
        console.error(`📅 [DATE CHECK] Error checking slot ${hour}:00:`, error)
        return { available: false, hour, error: error.message }
      }
    })

    // Wait for all slot checks to complete
    const results = await Promise.all(slotCheckPromises)

    // Check if ANY slot is available
    const availableSlot = results.find(r => r.available)

    const totalTime = performance.now() - startTime

    if (availableSlot) {
      debug.log(`📅 [DATE CHECK] ✅ Found available slot at ${availableSlot.hour}:00! (checked ${slots.length} slots in parallel in ${totalTime.toFixed(0)}ms)`)
      return true
    }

    // No available slots found
    debug.log(`📅 [DATE CHECK] ❌ No available slots found (checked ${slots.length} slots in parallel in ${totalTime.toFixed(0)}ms)`)
    return false

  } catch (error) {
    console.error('📅 [DATE CHECK] Error checking date availability:', error)
    return false // Return false on error to be safe
  }
}

/**
 * Get merchant data from API (with fallback to first available merchant)
 */
export async function getMerchantDataFromAPI(merchantId) {
  try {
    // Fetch all locations (will be filtered by merchantId if provided)
    const result = await fetchLocationsFromAPI(merchantId)
    const locations = result.locations
    const featuredPricingRules = result.featuredPricingRules

    if (!locations || locations.length === 0) {
      throw new Error(`No locations found for merchant: ${merchantId}`)
    }

    // Get merchant info from first location
    const firstLocation = locations[0]
    const merchantInfo = firstLocation._apiData?.merchant

    // Convert featured pricing rules to duration format (e.g., "9h", "1d", "2w")
    // Ensure featuredPricingRules is always an array (defensive coding)
    const safeFeaturePricingRules = Array.isArray(featuredPricingRules) ? featuredPricingRules : []

    const popularDurations = safeFeaturePricingRules.map(rule => {
      const value = Math.floor(rule.duration_value)

      if (rule.duration_type === 'hours') {
        return `${value}h`
      } else if (rule.duration_type === 'calendar_day' || rule.duration_type === 'multi_day') {
        return `${value}d`
      } else if (rule.duration_type === 'week') {
        return `${value}w`
      }
      return null
    }).filter(Boolean) // Remove any null values

    // Remove duplicates (in case multiple products have same featured duration)
    const uniquePopularDurations = [...new Set(popularDurations)]

    debug.log('Featured pricing rules from API:', featuredPricingRules)
    debug.log('Converted to popular durations:', uniquePopularDurations)

    // Don't fetch bikes at merchant level - will be fetched per selected location
    // Filter options will be generated dynamically based on selected location's bikes
    const bikes = []
    const filterOptions = {
      categories: [{ value: '', label: 'All Categories' }],
      bikeModels: ['All Models'],
      sizes: ['All Sizes']
    }

    // Add-ons are NOT fetched here — they are deferred to ProductSelection
    // where they load in parallel with bikes (non-blocking).
    const addons = {}

    // For now, we'll use a default config structure
    // In the future, merchant config should come from the database
    return {
      config: {
        id: merchantInfo?.merchant_id || merchantId || 'default',
        name: merchantInfo?.display_name || null, // No fallback - don't show if empty
        display_name: merchantInfo?.display_name || null, // For document title (NOT translatable) - no fallback
        business_name: merchantInfo?.business_name || null, // Translatable business name - no fallback
        description: merchantInfo?.description || null, // For meta tags (translatable)
        subtitle: merchantInfo?.business_name || null, // Legacy: same as business_name - no fallback
        supportEmail: merchantInfo?.email || 'support@bikerental.com', // Support email from database
        customMessage: merchantInfo?.custom_rental_periods_text || null, // Custom rental periods text from database (falls back to translation)
        logo_url: merchantInfo?.logo_url || null, // Merchant logo from database
        logo_width: merchantInfo?.logo_width || 40, // Logo width in pixels (40-200), height auto-calculated
        favicon_url: merchantInfo?.favicon_url || null, // Merchant favicon from database
        header_text_1: merchantInfo?.header_text_1 || null,
        header_text_2: merchantInfo?.header_text_2 || null,
        header_text_3: merchantInfo?.header_text_3 || null,
        color1: merchantInfo?.color1 || '#2563eb', // Primary color (replaces blue), default: blue-600
        color2: merchantInfo?.color2 || '#10b981', // Secondary color (replaces green), default: green-500
        deposits_enabled: merchantInfo?.deposits_enabled ?? false, // Whether security deposits are enabled (default false for safety)
        deposit_rules: merchantInfo?.deposit_rules || [], // Deposit rules from DB: [{amount, category_slug}]
        terms_pdf_url: merchantInfo?.terms_pdf_url || null, // URL to Terms of Service PDF
        cancellation_policy: merchantInfo?.cancellation_policy || null, // Cancellation policy JSON (language-neutral refund rules)
        cancellation_policy_description: merchantInfo?.cancellation_policy_description || null, // Default-language policy prose (flat column; mirrors merchant_translations)
        additional_text_checkout: merchantInfo?.additional_text_checkout || null, // Custom checkout agreement text
        // Stripe payment settings
        merchant_type: merchantInfo?.merchant_type || 'platform', // 'platform' or 'partner'
        stripe_connected_account_id: merchantInfo?.stripe_connected_account_id || null, // Stripe Connect account ID (only for partners)
        platform_fee_percentage: merchantInfo?.platform_fee_percentage || 0, // Platform fee percentage (whole number, e.g. 2 = 2%)
        platform_fee_fixed_cents: merchantInfo?.platform_fee_fixed_cents || 0, // Platform fee fixed component in currency smallest unit
        payment_method_types: merchantInfo?.payment_method_types || ['card'], // Stripe payment method types (e.g. ['card', 'pay_by_bank', 'eps']); filtered server-side via METHOD_RULES
        paypal_direct_enabled: merchantInfo?.paypal_direct_enabled ?? false, // Whether PayPal Direct (non-Stripe) is enabled
        // Currency and locale settings
        currency_code: merchantInfo?.currency_code || 'USD', // ISO 4217 currency code
        currency_symbol: merchantInfo?.currency_symbol || '$', // Currency symbol (auto-detected if not provided)
        timezone: merchantInfo?.timezone || 'America/New_York', // IANA timezone identifier
        country_code: merchantInfo?.country_code || null, // ISO 3166-1 alpha-2 (drives calendar week-start: US/CA = Sunday-first, else Monday)
        // Multi-language settings
        default_language: merchantInfo?.default_language || 'en', // Default language code (ISO 639-1)
        supported_languages: merchantInfo?.supported_languages || ['en'], // Array of supported language codes
        // Translations for non-default languages (from merchant_translations table)
        translations: merchantInfo?.merchant_translations || [], // Array of translation objects
        // Stripe payment settings
        off_session_payments: merchantInfo?.off_session_payments ?? false, // Enable off-session payments (save card for future use)
        debug: merchantInfo?.debug ?? false, // Enable debug logging in browser console
        consent_checkbox_label: merchantInfo?.consent_checkbox_label || null, // JSONB: {"en": "I agree to...", "de": "Ich stimme..."}
        booking_flow_variant: merchantInfo?.booking_flow_variant || 'default', // 'default' or 'start_end' — only affects pickup reservation flows
      },
      popularDurations: uniquePopularDurations, // From database pricing_rules where featured=true
      locations: locations,
      bikes: bikes, // Will be empty - bikes fetched per location
      addons: addons, // Add-ons for first location (can be re-fetched per location if needed)
      filterOptions: filterOptions, // Defaults - will be generated per location
      _fromAPI: true // Flag to indicate this is API data
    }
  } catch (error) {
    console.error('Error fetching merchant data from API:', error)
    throw error
  }
}

/**
 * Check if a location has a closing period on a given date
 * @param {string} locationId - The location ID
 * @param {Date} date - The date to check
 * @returns {Promise<{isClosed: boolean, reason: string|null}>} - Object with isClosed flag and reason
 */
export async function checkClosingPeriod(locationId, date) {
  try {
    // Format date as YYYY-MM-DD for comparison
    const dateStr = date.toISOString().split('T')[0];

    debug.log('🔒 [CLOSING PERIOD CHECK] Checking for closing periods:', {
      locationId,
      date: dateStr
    });

    const { data, error } = await supabase
      .from('closing_periods')
      .select('id, start_date, end_date, reason')
      .eq('location_id', locationId)
      .lte('start_date', dateStr)
      .gte('end_date', dateStr);

    if (error) throw error;

    if (data && data.length > 0) {
      debug.log('🔒 [CLOSING PERIOD CHECK] Found closing period:', data[0]);
      return {
        isClosed: true,
        reason: data[0].reason || 'Location is temporarily closed'
      };
    }

    debug.log('🔒 [CLOSING PERIOD CHECK] No closing period found');
    return { isClosed: false, reason: null };
  } catch (error) {
    console.error('🔒 [CLOSING PERIOD CHECK] Error checking closing period:', error);
    // On error, don't block the booking - assume not closed
    return { isClosed: false, reason: null };
  }
}
